import { ListFilter } from "lucide-react";
import { useFeatureFlag } from "@/services/FeatureFlags/FeatureFlags";
import { isRecord } from "@/utils/isRecord";
import { type GenieTool } from "../registries/tool-registry";
import { useGenieContext } from "../use-genie-context";
import { useGenieTools } from "../use-genie-tools";

// The backend hides Genie's case tools and guide while this is off, so the
// case pages hand Genie neither their tools nor their context then.
export function useGenieCasesEnabled(): boolean {
  return useFeatureFlag("GENIE_CASES_ENABLED").value === true;
}

export const CASE_STATUSES = [
  "open",
  "pending",
  "closed",
  "cancelled",
  "excluded",
] as const;
export const TASK_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "failed",
] as const;
export const PRIORITIES = ["low", "medium", "high", "urgent"] as const;
export const TASK_TYPES = [
  "reply",
  "approval",
  "field_extraction",
  "default",
] as const;
// The columns the cases list's header sorts by, as cases_list names them.
export const CASES_SORT_COLUMNS = [
  "created_at",
  "closed_at",
  "status",
] as const;
// The columns the tasks list's header sorts by.
export const TASKS_SORT_COLUMNS = ["updated_at"] as const;

export type CaseStatus = (typeof CASE_STATUSES)[number];
export type TaskStatus = (typeof TASK_STATUSES)[number];
export type Priority = (typeof PRIORITIES)[number];
export type TaskType = (typeof TASK_TYPES)[number];
export type CasesSortColumn = (typeof CASES_SORT_COLUMNS)[number];
export type TasksSortColumn = (typeof TASKS_SORT_COLUMNS)[number];

export interface CasesSort {
  column: CasesSortColumn;
  direction: "asc" | "desc";
}

export interface TasksSort {
  column: TasksSortColumn;
  direction: "asc" | "desc";
}

export interface CasesListChange {
  search?: string;
  // Empty shows every status, as the list starts.
  statuses?: CaseStatus[];
  priorities?: Priority[];
  sort?: CasesSort | null;
}

export interface CasesListDeps {
  setView: (change: CasesListChange) => void;
  // How many cases the list would show once changed.
  countCases: (change: CasesListChange) => Promise<number>;
}

export interface CasesViewState {
  inbox_id: string | null;
  search: string;
  statuses: string[];
  priorities: string[];
  sort: CasesSort | null;
  page: number;
  page_size: number;
  total: number | null;
  on_screen: {
    id: string;
    display_id: string | null;
    subject: string;
    status: string;
    priority: string;
  }[];
}

export interface TasksListChange {
  search?: string;
  statuses?: TaskStatus[];
  priorities?: Priority[];
  types?: TaskType[];
  sort?: TasksSort | null;
}

export interface TasksListCount {
  // Matching search, status and priority, across every page.
  total: number;
  // Of the page the list will show, those of the chosen types: the type
  // filter narrows the page on screen only.
  ofTypeOnPage: number | null;
}

export interface TasksListDeps {
  setView: (change: TasksListChange) => void;
  countTasks: (change: TasksListChange) => Promise<TasksListCount>;
}

export interface TasksViewState {
  inbox_id: string | null;
  search: string;
  statuses: string[];
  priorities: string[];
  types: string[];
  sort: TasksSort | null;
  page: number;
  page_size: number;
  total: number | null;
  on_screen: {
    id: string;
    title: string | null;
    task_type: string;
    status: string;
    case_display_id: string | null;
  }[];
}

type Read<T> = { ok: T } | { error: string };

function readList<T extends string>(
  raw: unknown,
  name: string,
  allowed: readonly T[]
): Read<T[] | undefined> {
  if (raw === undefined) return { ok: undefined };
  if (raw === null) return { ok: [] };
  if (!Array.isArray(raw)) return { error: `${name} must be a list.` };
  const values: T[] = [];
  for (const item of raw) {
    const value = allowed.find((a) => a === item);
    if (!value) {
      return {
        error: `"${String(item)}" is not one of the ${name}: ${allowed.join(", ")}.`,
      };
    }
    values.push(value);
  }
  return { ok: values };
}

const readSearch = (raw: unknown): string | undefined =>
  raw === undefined ? undefined : raw === null ? "" : String(raw);

function readSort<C extends string>(
  raw: unknown,
  allowed: readonly C[]
): Read<{ column: C; direction: "asc" | "desc" } | null | undefined> {
  if (raw === undefined) return { ok: undefined };
  if (raw === null) return { ok: null };
  if (!isRecord(raw)) {
    return { error: "sort is {column, direction}, or null to clear it." };
  }
  const column = allowed.find((c) => c === raw.column);
  if (!column) return { error: `Sort by one of ${allowed.join(", ")}.` };
  return {
    ok: { column, direction: raw.direction === "asc" ? "asc" : "desc" },
  };
}

function readCasesChange(
  input: Record<string, unknown>
): Read<CasesListChange> {
  const change: CasesListChange = {};
  const search = readSearch(input.search);
  if (search !== undefined) change.search = search;
  const statuses = readList(input.statuses, "statuses", CASE_STATUSES);
  if ("error" in statuses) return statuses;
  if (statuses.ok) change.statuses = statuses.ok;
  const priorities = readList(input.priorities, "priorities", PRIORITIES);
  if ("error" in priorities) return priorities;
  if (priorities.ok) change.priorities = priorities.ok;
  const sort = readSort(input.sort, CASES_SORT_COLUMNS);
  if ("error" in sort) return sort;
  if (sort.ok !== undefined) change.sort = sort.ok;
  if (Object.keys(change).length === 0) {
    return {
      error: "Nothing to change: pass search, statuses, priorities or sort.",
    };
  }
  return { ok: change };
}

function readTasksChange(
  input: Record<string, unknown>
): Read<TasksListChange> {
  const change: TasksListChange = {};
  const search = readSearch(input.search);
  if (search !== undefined) change.search = search;
  const statuses = readList(input.statuses, "statuses", TASK_STATUSES);
  if ("error" in statuses) return statuses;
  if (statuses.ok) change.statuses = statuses.ok;
  const priorities = readList(input.priorities, "priorities", PRIORITIES);
  if ("error" in priorities) return priorities;
  if (priorities.ok) change.priorities = priorities.ok;
  const types = readList(input.types, "types", TASK_TYPES);
  if ("error" in types) return types;
  if (types.ok) change.types = types.ok;
  const sort = readSort(input.sort, TASKS_SORT_COLUMNS);
  if ("error" in sort) return sort;
  if (sort.ok !== undefined) change.sort = sort.ok;
  if (Object.keys(change).length === 0) {
    return {
      error:
        "Nothing to change: pass search, statuses, priorities, types or sort.",
    };
  }
  return { ok: change };
}

const failed = (what: string, e: unknown) => {
  console.warn(`[genie] counting the ${what} failed`, e);
  return {
    error: `Counting the ${what} failed, so the list is unchanged. Try again, or ask the user to refresh the page.`,
  };
};

export function buildCasesListTools(deps: CasesListDeps): GenieTool[] {
  return [
    {
      name: "cases_list_set_view",
      description:
        "Change what the cases list on screen shows, as its own search box, Status and Priority filters and column sort do: " +
        "search (display id, subject or status), statuses (the list replaces the filter; [] shows every status), " +
        "priorities ([] clears the filter) and sort (created_at, closed_at or status). " +
        "When the user asks to see, find, filter, sort or count cases while on this list, show the answer in the list with this tool " +
        "rather than as a list in chat, then say in one line what it shows and how many cases match. " +
        "Send only the fields this request changes; an omitted field keeps what is on screen. " +
        "The cases_view context is what the list shows now. It changes only the view, never a case.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          statuses: {
            type: "array",
            items: { type: "string", enum: [...CASE_STATUSES] },
          },
          priorities: {
            type: "array",
            items: { type: "string", enum: [...PRIORITIES] },
          },
          sort: {
            type: ["object", "null"],
            properties: {
              column: { type: "string", enum: [...CASES_SORT_COLUMNS] },
              direction: { type: "string", enum: ["asc", "desc"] },
            },
            required: ["column", "direction"],
          },
        },
      },
      execute: async (input) => {
        const read = readCasesChange(input);
        if ("error" in read) return read;
        let matching: number;
        try {
          matching = await deps.countCases(read.ok);
        } catch (e) {
          return failed("cases", e);
        }
        deps.setView(read.ok);
        return { matching_cases: matching };
      },
      display: {
        icon: ListFilter,
        messageStart: () => "Changing the cases list...",
        messageEnd: () => "Changed the cases list",
        messageError: "Could not change the cases list",
      },
    },
  ];
}

export function buildTasksListTools(deps: TasksListDeps): GenieTool[] {
  return [
    {
      name: "tasks_list_set_view",
      description:
        "Change what the tasks list on screen shows, as its own search box, Status, Priority and Type filters and column sort do: " +
        "search (task title, id or case subject), statuses, priorities and types (each list replaces its filter; [] clears it) " +
        "and sort (updated_at, the Last modified column; null goes back to newest first). " +
        "As on screen, priorities filters only when it names one priority. " +
        "The type filter narrows only the page on screen, not the whole list: the tool returns how many tasks match the rest " +
        "(matching_tasks) and, with types, how many tasks of those types the page on screen shows (of_type_on_this_page). " +
        "When the user asks to see, find, filter, sort or count tasks while on this list, show the answer in the list with this tool, " +
        "then say in one line what it shows. Send only the fields this request changes. " +
        "The tasks_view context is what the list shows now. It changes only the view, never a task.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          statuses: {
            type: "array",
            items: { type: "string", enum: [...TASK_STATUSES] },
          },
          priorities: {
            type: "array",
            items: { type: "string", enum: [...PRIORITIES] },
          },
          types: {
            type: "array",
            items: { type: "string", enum: [...TASK_TYPES] },
          },
          sort: {
            type: ["object", "null"],
            properties: {
              column: { type: "string", enum: [...TASKS_SORT_COLUMNS] },
              direction: { type: "string", enum: ["asc", "desc"] },
            },
            required: ["column", "direction"],
          },
        },
      },
      execute: async (input) => {
        const read = readTasksChange(input);
        if ("error" in read) return read;
        let count: TasksListCount;
        try {
          count = await deps.countTasks(read.ok);
        } catch (e) {
          return failed("tasks", e);
        }
        deps.setView(read.ok);
        return count.ofTypeOnPage === null
          ? { matching_tasks: count.total }
          : {
              matching_tasks: count.total,
              of_type_on_this_page: count.ofTypeOnPage,
            };
      },
      display: {
        icon: ListFilter,
        messageStart: () => "Changing the tasks list...",
        messageEnd: () => "Changed the tasks list",
        messageError: "Could not change the tasks list",
      },
    },
  ];
}

// The tools register again whenever ``deps``
// changes, so the caller keeps it stable: its functions read the list when
// they run.
export function useCasesListTools(
  deps: CasesListDeps,
  view: CasesViewState,
  enabled: boolean
): void {
  useGenieContext(
    "cases_view",
    "What the cases list on screen shows: its inbox, search, filters, sort, page, total and the cases on the page",
    view,
    enabled
  );
  useGenieTools(() => buildCasesListTools(deps), [deps], enabled);
}

export function useTasksListTools(
  deps: TasksListDeps,
  view: TasksViewState,
  enabled: boolean
): void {
  useGenieContext(
    "tasks_view",
    "What the tasks list on screen shows: its inbox, search, filters, sort, page, total and the tasks on the page",
    view,
    enabled
  );
  useGenieTools(() => buildTasksListTools(deps), [deps], enabled);
}
